import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Checkbox,
  Container,
  Dropdown,
  Grid,
  Icon,
  Input,
  Label,
  Message,
  Modal,
  Pagination,
  Segment,
} from 'semantic-ui-react';
import images from '../../images';
import { useAuth } from '../context/AuthContext';
import { stockyApi } from '../misc/StockyApi';
import { useNotifications } from '../../hooks/useNotifications';
import { useMarketplaceSearch } from '../../hooks/useMarketplaceSearch';
import SemanticUiReactSnackbar from '../misc/SemanticUiReactSnackbar';
import {
  pageBodyStyle,
  pageFrameStyle,
  pageShellStyle,
  surfaceCardStyle,
} from '../misc/pageStyles';

const TAB_KEYS = {
  NOTIFICATIONS: 'notifications',
  SEARCH: 'search',
};

const marketplaceOptions = [
  { key: 'all', text: 'Todos los canales', value: '' },
  { key: 'mercado_libre', text: 'Mercado Libre', value: 'mercado_libre' },
  { key: 'tienda_nube', text: 'Tienda Nube', value: 'tienda_nube' },
];

const searchRemoteStatusOptions = [
  { key: 'all-remote', text: 'Todos los estados remotos', value: '' },
  { key: 'active-remote', text: 'Activas', value: 'active' },
  { key: 'paused-remote', text: 'Pausadas', value: 'paused' },
  { key: 'closed-remote', text: 'Cerradas', value: 'closed' },
  { key: 'under-review-remote', text: 'Under review', value: 'under_review' },
  { key: 'unpublished-remote', text: 'No publicadas', value: 'unpublished' },
];

const notificationStatusOptions = [
  { key: 'pending', text: 'Pendientes', value: 'pending' },
  { key: 'all', text: 'Todos', value: '' },
  { key: 'failed', text: 'Con error', value: 'failed' },
  { key: 'dismissed', text: 'Descartados', value: 'dismissed' },
  { key: 'imported', text: 'Importados', value: 'imported' },
];

const searchMercuryStateOptions = [
  { key: 'all', text: 'Todos los estados', value: '' },
  { key: 'new-item', text: 'SKU y listing nuevos', value: 'new-item' },
  { key: 'existing-sku', text: 'SKU existente', value: 'existing-sku' },
  { key: 'linked', text: 'Listing vinculado', value: 'linked' },
  { key: 'error', text: 'Error de importación', value: 'error' },
];

const searchSortOptions = [
  { key: 'recent', text: 'Más recientes', value: 'recent' },
  { key: 'name', text: 'Nombre', value: 'name' },
  { key: 'state', text: 'Estado', value: 'state' },
  { key: 'price', text: 'Precio', value: 'price' },
];

const initialNotificationFilters = {
  searchInput: '',
  search: '',
  marketplace: '',
  status: 'pending',
  sellerId: '',
  sort: 'recent',
};

const initialSearchFilters = {
  titleInput: '',
  skuInput: '',
  title: '',
  sku: '',
  marketplace: '',
  sellerId: '',
  state: '',
  remoteStatus: '',
  category: '',
  minPrice: '',
  maxPrice: '',
  sort: 'recent',
};

const PARTIAL_SEARCH_MIN_LENGTH = 3;

const headerWrapStyle = {
  position: 'relative',
  padding: '18px 24px 16px',
  background: '#FFFFFF',
  borderBottom: '1px solid #ECEFF3',
};

const headerAccentStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  height: 3,
  background: 'linear-gradient(90deg, #7E22CE 0%, #C026D3 100%)',
};

const headerRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  flexWrap: 'wrap',
};

const headerLeadStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 18,
  flexWrap: 'wrap',
};

const headerIconStyle = {
  width: 44,
  height: 44,
  borderRadius: 14,
  border: '1px solid #E7DDFC',
  background: '#F7F1FF',
  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#7C3AED',
  flexShrink: 0,
};

const headerTitleStyle = {
  fontSize: 24,
  fontWeight: 700,
  color: '#111827',
  marginBottom: 4,
};

const headerDescriptionStyle = {
  fontSize: 14,
  color: '#667085',
  lineHeight: 1.45,
};

const tabsWrapStyle = {
  marginTop: 16,
  padding: 4,
  display: 'flex',
  gap: 8,
  flexWrap: 'wrap',
  background: '#FFFFFF',
  border: '1px solid #E8ECF2',
  borderRadius: 16,
  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)',
};

const tabButtonStyle = (active) => ({
  flex: 1,
  minWidth: 220,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  padding: '12px 14px',
  borderRadius: 12,
  border: active ? '1px solid #DDD6FE' : '1px solid transparent',
  borderBottom: active ? '2px solid #7C3AED' : '1px solid transparent',
  background: active ? '#F7F2FF' : '#FFFFFF',
  boxShadow: 'none',
  cursor: 'pointer',
});

const summaryWrapperStyle = {
  background: '#FFFFFF',
  border: '1px solid #E8ECF2',
  borderRadius: 16,
  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)',
  padding: 0,
  overflow: 'hidden',
  marginTop: 16,
};

const summaryColumnStyle = {
  padding: '18px 16px',
  minHeight: 92,
};

const summaryMetricStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 14,
};

const summaryIconBoxStyle = {
  width: 46,
  height: 46,
  borderRadius: 14,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const summaryCountStyle = {
  fontSize: 26,
  fontWeight: 700,
  lineHeight: 1,
  color: '#111827',
};

const summaryLabelStyle = {
  fontSize: 15,
  fontWeight: 700,
  color: '#374151',
  marginTop: 6,
};

const summaryHelperStyle = {
  fontSize: 14,
  color: '#6B7280',
  marginTop: 4,
};

const actionBarStyle = {
  background: '#FFFFFF',
  border: '1px solid #E8ECF2',
  borderRadius: 16,
  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)',
  padding: 12,
  marginTop: 16,
};

const filterBarStyle = {
  background: '#FFFFFF',
  border: '1px solid #E8ECF2',
  borderRadius: 16,
  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)',
  padding: 14,
  marginTop: 16,
};

const searchHeroStyle = {
  background: '#FFFFFF',
  border: '1px solid #E8ECF2',
  borderRadius: 16,
  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)',
  padding: 18,
  marginTop: 16,
};

const listPanelStyle = {
  background: '#FFFFFF',
  border: '1px solid #E8ECF2',
  borderRadius: 16,
  boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)',
  padding: 0,
  overflow: 'hidden',
  marginTop: 16,
};

const listScrollWrapStyle = {
  width: '100%',
  overflowX: 'auto',
};

const listHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '16px 20px',
  borderBottom: '1px solid #EEF2F6',
  flexWrap: 'wrap',
};

const listColumnHintStyle = {
  display: 'grid',
  gridTemplateColumns:
    '36px 76px minmax(280px, 2.2fr) minmax(220px, 1.3fr) minmax(170px, 1fr) minmax(180px, 1fr) minmax(180px, 1fr)',
  gap: 12,
  padding: '10px 20px',
  fontSize: 12,
  fontWeight: 600,
  color: '#6B7280',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  background: '#F8FAFC',
  borderBottom: '1px solid #EEF2F6',
};

const listRowStyle = {
  padding: '16px 20px',
  background: '#FFFFFF',
  borderBottom: '1px solid #EEF2F6',
};

const listRowGridStyle = {
  display: 'grid',
  gridTemplateColumns:
    '36px 76px minmax(280px, 2.2fr) minmax(220px, 1.3fr) minmax(170px, 1fr) minmax(180px, 1fr) minmax(180px, 1fr)',
  gap: 12,
  alignItems: 'center',
};

const thumbnailTileStyle = {
  width: 74,
  height: 74,
  borderRadius: 12,
  border: '1px solid #E5E7EB',
  background: '#F8FAFC',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#7C3AED',
  flexShrink: 0,
  overflow: 'hidden',
};

const itemTitleStyle = {
  fontSize: 16,
  fontWeight: 600,
  color: '#111827',
  lineHeight: 1.3,
};

const channelCellStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 10,
};

const secondaryMetaStyle = {
  color: '#667085',
  fontSize: 14,
};

const infoMetaStyle = {
  color: '#6B7280',
  fontSize: 13,
  lineHeight: 1.45,
};

const emptyStateStyle = {
  borderRadius: 16,
  border: '1px dashed #D8DDE6',
  background: '#FCFDFE',
  padding: '28px 20px',
  margin: 20,
  textAlign: 'center',
};

const errorStateStyle = {
  borderRadius: 16,
  border: '1px solid #E9D5FF',
  background: '#FFFDFE',
  padding: 16,
};

const footerRowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 12,
  padding: '18px 22px',
  flexWrap: 'wrap',
};

const importChoiceCardStyle = (selected) => ({
  borderRadius: 16,
  border: selected ? '1px solid #C084FC' : '1px solid #E9D5FF',
  background: selected ? '#FBF7FF' : '#FFFFFF',
  boxShadow: selected ? '0 6px 18px rgba(15, 23, 42, 0.04)' : 'none',
  padding: 18,
  cursor: 'pointer',
  height: '100%',
});

const pageBackgroundStyle = {
  ...pageShellStyle,
  maxWidth: '100%',
  width: '100%',
  background: '#F8F9FB',
};

const panelFrameStyle = {
  ...pageFrameStyle,
  background: '#F8F9FB',
  border: 'none',
  boxShadow: 'none',
};

const primaryButtonStyle = {
  background: '#7C3AED',
  color: '#FFFFFF',
  borderRadius: 10,
  boxShadow: 'none',
  fontWeight: 600,
};

const secondaryButtonStyle = {
  borderRadius: 10,
  border: '1px solid #D8C8F8',
  color: '#6D28D9',
  background: '#FFFFFF',
  boxShadow: 'none',
  fontWeight: 600,
};

const neutralButtonStyle = {
  borderRadius: 10,
  border: '1px solid #E5E7EB',
  color: '#4B5563',
  background: '#FFFFFF',
  boxShadow: 'none',
  fontWeight: 600,
};

const rowActionButtonStyle = {
  minWidth: 116,
  justifyContent: 'center',
};

const subtleBadgeStyle = (kind) => {
  if (kind === 'green') {
    return { background: '#ECFDF3', color: '#166534', borderColor: '#CDEED8' };
  }
  if (kind === 'yellow') {
    return { background: '#FFF7E8', color: '#92400E', borderColor: '#F6D8A8' };
  }
  if (kind === 'red') {
    return { background: '#FEF2F2', color: '#B42318', borderColor: '#F5C9C7' };
  }
  return { background: '#F3F4F6', color: '#4B5563', borderColor: '#E5E7EB' };
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return new Intl.DateTimeFormat('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatRelativeTime = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return formatDateTime(value);

  const diffMs = date.getTime() - Date.now();
  const formatter = new Intl.RelativeTimeFormat('es', { numeric: 'auto' });
  const minutes = Math.round(diffMs / (1000 * 60));
  const hours = Math.round(diffMs / (1000 * 60 * 60));
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (Math.abs(minutes) < 60) return formatter.format(minutes, 'minute');
  if (Math.abs(hours) < 24) return formatter.format(hours, 'hour');
  return formatter.format(days, 'day');
};

const formatLatestDetection = (value) => {
  if (!value) {
    return { primary: '-', secondary: 'Sin actividad reciente' };
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return { primary: String(value), secondary: 'Detección registrada' };
  }

  return {
    primary: new Intl.DateTimeFormat('es-AR', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date),
    secondary: new Intl.DateTimeFormat('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date),
  };
};

const formatCurrency = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(amount);
};

const normalizeText = (value) => String(value || '').trim().toLowerCase();

const getMarketplaceLabel = (value) => {
  if (value === 'mercado_libre') return 'Mercado Libre';
  if (value === 'tienda_nube') return 'Tienda Nube';
  return value || '-';
};

const getMarketplaceLogo = (value) => {
  if (value === 'mercado_libre') return images.meliIcon;
  if (value === 'tienda_nube') return images.tnIcon;
  return null;
};

const getSummaryVisual = (key) => {
  if (key === 'pending') {
    return { icon: 'file outline', background: '#F5EDFF', color: '#7C3AED' };
  }
  if (key === 'unread') {
    return { icon: 'mail outline', background: '#FFF6DD', color: '#F59E0B' };
  }
  if (key === 'importedToday') {
    return { icon: 'check circle outline', background: '#EAFBF1', color: '#22C55E' };
  }
  return { icon: 'clock outline', background: '#EEF2FF', color: '#6480C7' };
};

const getMarketplaceIconName = (value) => {
  if (value === 'mercado_libre') return 'shopping bag';
  if (value === 'tienda_nube') return 'cloud';
  return 'tag';
};

const getThumbnailLabel = (row) => {
  const source = String(row?.title || row?.sku || row?.marketplaceId || '?');
  return source.trim().slice(0, 1).toUpperCase();
};

const getCandidateStatusLabel = (status) => {
  if (status === 'pending') return 'Pendiente';
  if (status === 'failed') return 'Con error';
  if (status === 'dismissed') return 'Descartado';
  if (status === 'imported') return 'Importado';
  if (status === 'importing') return 'Importando';
  if (status === 'active') return 'Activo';
  return status || '-';
};

const getSellerAccountType = (seller) =>
  normalizeText(seller?.accountType || seller?.account_type);

const toNumberOrNull = (value) => {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getSnapshot = (row) =>
  row?.rawItemSnapshot && typeof row.rawItemSnapshot === 'object' ? row.rawItemSnapshot : {};

const getObservedPrice = (row) => {
  const direct = toNumberOrNull(row?.price);
  if (direct !== null) return direct;
  const snapshot = getSnapshot(row);
  return toNumberOrNull(snapshot?.price);
};

const getObservedStock = (row) => {
  const direct = toNumberOrNull(row?.availableQuantity);
  if (direct !== null) return direct;
  const meta = toNumberOrNull(row?.metadata?.observedAvailableQuantity);
  if (meta !== null) return meta;
  const snapshot = getSnapshot(row);
  return toNumberOrNull(snapshot?.available_quantity);
};

const getObservedCategory = (row) => {
  const direct = String(row?.categoryId || '').trim();
  if (direct) return direct;
  const snapshot = getSnapshot(row);
  return String(snapshot?.category_id || '').trim();
};

const getMercuryStock = (row) => {
  const direct = toNumberOrNull(row?.existingItemAvailableQuantity);
  if (direct !== null) return direct;
  return null;
};

const getThumbnailUrl = (row) => {
  const direct = String(row?.thumbnailUrl || '').trim();
  if (direct) return direct;
  const pictures = Array.isArray(getSnapshot(row)?.pictures) ? getSnapshot(row).pictures : [];
  const first = pictures[0];
  return String(first?.secure_url || first?.url || '').trim() || null;
};

const getProductTitle = (row) =>
  row?.variationName
    ? `${row.title || 'Producto sin título'} - ${row.variationName}`
    : row?.title || 'Producto sin título';

const getSmartState = (row, tabKey) => {
  const hasError = row?.status === 'failed';
  const isLinked =
    row?.status === 'imported' || row?.alreadyImported === true || Boolean(row?.matchedListingId);
  const hasExistingSku = !isLinked && row?.existingSkuMatch === true;

  if (hasError) {
    return {
      key: 'error',
      badgeColor: 'red',
      label: 'Error de importación',
      helper: row?.lastError || 'Falló la última importación',
      action: 'Reintentar',
      actionKind: 'import',
    };
  }

  if (isLinked) {
    return {
      key: 'linked',
      badgeColor: 'green',
      label: 'Existe publicación',
      helper: 'La publicación ya existe en MercuryCom',
      action: 'Ver detalle',
      actionKind: 'detail',
    };
  }

  if (hasExistingSku) {
    return {
      key: 'existing-sku',
      badgeColor: 'yellow',
      label: 'Existe SKU en MercuryCom',
      helper: 'Solo falta importar la publicación',
      action: 'Importar',
      actionKind: 'import',
    };
  }

  return {
    key: 'new-item',
    badgeColor: tabKey === TAB_KEYS.NOTIFICATIONS ? 'red' : 'red',
    label: 'No existe SKU ni publicación',
    helper: 'Se creará el item y la publicación',
    action: 'Importar',
    actionKind: 'import',
  };
};

const getMercuryResolutionPriority = (row, tabKey) => {
  const smartState = getSmartState(row, tabKey);
  if (smartState.key === 'linked') return 2;
  return 1;
};

const buildSellerOptions = (sellers, marketplace) => {
  const normalizedMarketplace = normalizeText(marketplace);
  const filtered = Array.isArray(sellers)
    ? sellers.filter((seller) => {
        if (!normalizedMarketplace) return true;
        const accountType = getSellerAccountType(seller);
        return accountType === normalizedMarketplace;
      })
    : [];

  return [
    { key: 'all', text: 'Todos los sellers', value: '' },
    ...filtered.map((seller) => ({
      key: String(seller.id),
      text: `${seller.nickname || seller.name || seller.id} · ${getMarketplaceLabel(
        seller.accountType || seller.account_type,
      )}`,
      value: String(seller.id),
    })),
  ];
};

const buildSearchDetailPayload = (candidate) => ({
  marketplace: getMarketplaceLabel(candidate.marketplace),
  cuenta: candidate.sellerName || candidate.sellerId || '-',
  referenciaMarketplace: candidate.marketplaceId || '-',
  titulo: candidate.title || 'Producto sin título',
  variacion: candidate.variationName || null,
  sku: candidate.sku || 'Sin SKU',
  estadoMarketplace: candidate.status || '-',
  precioObservado: getObservedPrice(candidate),
  stockObservado: getObservedStock(candidate),
  categoria: getObservedCategory(candidate) || null,
  estadoEnMercuryCom: getSmartState(candidate, TAB_KEYS.SEARCH).label,
  listingVinculado: candidate.alreadyImported === true || Boolean(candidate.matchedListingId),
  skuYaExisteEnMercuryCom: candidate.existingSkuMatch === true,
  productoMercuryCom: candidate.existingItemTitle || null,
  thumbnailUrl: getThumbnailUrl(candidate),
});

const buildDetectedDetailPayload = (candidate) => ({
  marketplace: getMarketplaceLabel(candidate.marketplace),
  cuenta: candidate.sellerName || candidate.sellerId || '-',
  referenciaMarketplace: candidate.marketplaceId || '-',
  titulo: candidate.title || 'Producto sin título',
  sku: candidate.sku || 'Sin SKU',
  estadoMercado: candidate.status || '-',
  precioObservado: getObservedPrice(candidate),
  stockObservado: getObservedStock(candidate),
  categoria: getObservedCategory(candidate) || null,
  estadoEnMercuryCom: getSmartState(candidate, TAB_KEYS.NOTIFICATIONS).label,
  skuYaExisteEnMercuryCom: candidate.existingSkuMatch === true,
  productoMercuryCom: candidate.existingItemTitle || null,
  listingVinculado: Boolean(candidate.matchedListingId),
  metadata: candidate.metadata || {},
});

function NotificationsDashboard() {
  const navigate = useNavigate();
  const { getUser } = useAuth();
  const currentUser = getUser();
  const authUser = useMemo(() => {
    if (!currentUser?.accessToken) return null;
    return { accessToken: currentUser.accessToken };
  }, [currentUser?.accessToken]);

  const [activeTab, setActiveTab] = useState(TAB_KEYS.SEARCH);
  const [searchAdvancedOpen, setSearchAdvancedOpen] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [message, setMessage] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarVariant, setSnackbarVariant] = useState('success');
  const [busyAction, setBusyAction] = useState('');
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [selectedNotificationIds, setSelectedNotificationIds] = useState([]);
  const [selectedSearchKeys, setSelectedSearchKeys] = useState([]);
  const [importDecisionModal, setImportDecisionModal] = useState({
    open: false,
    source: null,
    ids: [],
    rows: [],
    option: 'use_mercury',
  });

  const handleVisibleNotificationRowsChange = useCallback((rows) => {
    const visibleIds = new Set(
      (Array.isArray(rows) ? rows : [])
        .map((row) => row?.id)
        .filter(Boolean),
    );
    setSelectedNotificationIds((prev) => prev.filter((id) => visibleIds.has(id)));
  }, []);

  const {
    candidateData,
    notificationPage,
    setNotificationPage,
    notificationFilters,
    setNotificationFilters,
    loadingNotifications,
    notificationsError,
    summary,
    loadNotifications,
  } = useNotifications(authUser, initialNotificationFilters, {
    onRowsChange: handleVisibleNotificationRowsChange,
  });

  const {
    searchFilters,
    setSearchFilters,
    searchLoading,
    searchError,
    setSearchError,
    searchResults,
    setSearchResults,
    searchHasExecuted,
    setSearchHasExecuted,
    searchPage,
    setSearchPage,
    searchMeta,
    handleSearchMarketplace,
  } = useMarketplaceSearch(authUser, initialSearchFilters);

  useEffect(() => {
    if (!authUser) {
      navigate('/login');
    }
  }, [authUser, navigate]);

  useEffect(() => {
    if (!authUser) return;
    let mounted = true;
    stockyApi
      .getSellers(authUser)
      .then((response) => {
        if (!mounted) return;
        setSellers(Array.isArray(response?.data) ? response.data : []);
      })
      .catch((error) => {
        console.error('Error loading sellers for notifications screen:', error);
      });
    return () => {
      mounted = false;
    };
  }, [authUser]);

  const handleSearchMercadoLibre = async (pageOverride = 1) => {
    const result = await handleSearchMarketplace(pageOverride);
    if (result.warning) {
      setMessage({ type: 'warning', content: result.warning });
    } else if (result.success) {
      setMessage(null);
      setSelectedSearchKeys([]);
    }
  };

  const notificationRows = useMemo(() => {
    const rows = Array.isArray(candidateData.data) ? [...candidateData.data] : [];
    return rows.sort((left, right) => {
      const priorityDelta =
        getMercuryResolutionPriority(left, TAB_KEYS.NOTIFICATIONS) -
        getMercuryResolutionPriority(right, TAB_KEYS.NOTIFICATIONS);
      if (priorityDelta !== 0) return priorityDelta;

      if (notificationFilters.sort === 'recent') {
        return (
          new Date(right.lastDetectedAt).getTime() - new Date(left.lastDetectedAt).getTime()
        );
      }
      return 0;
    });
  }, [candidateData.data, notificationFilters.sort]);

  const rawSearchRows = useMemo(
    () =>
      (Array.isArray(searchResults) ? searchResults : []).map((result) => ({
        ...result,
        id: `${result.tokenId}:${result.marketplaceId}`,
        marketplace: result.marketplace || 'mercado_libre',
        lastDetectedAt: null,
        metadata: {
          ...(result.metadata || {}),
          eventType: 'manual_search',
        },
      })),
    [searchResults],
  );

  const categoryOptions = useMemo(() => {
    const unique = Array.from(
      new Set(rawSearchRows.map((row) => getObservedCategory(row)).filter(Boolean)),
    );
    return [
      { key: 'all', text: 'Todas las categorías', value: '' },
      ...unique.map((categoryId) => ({
        key: categoryId,
        text: categoryId,
        value: categoryId,
      })),
    ];
  }, [rawSearchRows]);

  const filteredSearchRows = useMemo(() => {
    const rows = rawSearchRows.filter((row) => {
      if (searchFilters.marketplace && row.marketplace !== searchFilters.marketplace) {
        return false;
      }
      if (searchFilters.sellerId && String(row.sellerId || '') !== searchFilters.sellerId) {
        return false;
      }
      if (searchFilters.remoteStatus && String(row.status || '') !== searchFilters.remoteStatus) {
        return false;
      }
      const smartState = getSmartState(row, TAB_KEYS.SEARCH);
      if (searchFilters.state && smartState.key !== searchFilters.state) {
        return false;
      }
      const category = getObservedCategory(row);
      if (searchFilters.category && category !== searchFilters.category) {
        return false;
      }
      const price = getObservedPrice(row);
      const minPrice = toNumberOrNull(searchFilters.minPrice);
      const maxPrice = toNumberOrNull(searchFilters.maxPrice);
      if (minPrice !== null && (price === null || price < minPrice)) {
        return false;
      }
      if (maxPrice !== null && (price === null || price > maxPrice)) {
        return false;
      }
      return true;
    });

    return rows.sort((left, right) => {
      const priorityDelta =
        getMercuryResolutionPriority(left, TAB_KEYS.SEARCH) -
        getMercuryResolutionPriority(right, TAB_KEYS.SEARCH);
      if (priorityDelta !== 0) return priorityDelta;

      if (searchFilters.sort === 'name') {
        return getProductTitle(left).localeCompare(getProductTitle(right), 'es');
      }
      if (searchFilters.sort === 'state') {
        return getSmartState(left, TAB_KEYS.SEARCH).label.localeCompare(
          getSmartState(right, TAB_KEYS.SEARCH).label,
          'es',
        );
      }
      if (searchFilters.sort === 'price') {
        return (getObservedPrice(right) || 0) - (getObservedPrice(left) || 0);
      }
      return 0;
    });
  }, [rawSearchRows, searchFilters]);

  const notificationSellerOptions = useMemo(
    () => buildSellerOptions(sellers, notificationFilters.marketplace),
    [sellers, notificationFilters.marketplace],
  );

  const searchSellerOptions = useMemo(
    () => buildSellerOptions(sellers, searchFilters.marketplace),
    [sellers, searchFilters.marketplace],
  );

  const latestDetection = useMemo(() => {
    const source = [...notificationRows]
      .map((row) => row.lastDetectedAt)
      .filter(Boolean)
      .sort((left, right) => new Date(right).getTime() - new Date(left).getTime());
    return source[0] || summary.latest?.[0]?.createdAt || null;
  }, [notificationRows, summary.latest]);

  const importedToday = useMemo(() => {
    const now = new Date();
    return notificationRows.filter((row) => {
      if (!row.importedAt || row.status !== 'imported') return false;
      const importedDate = new Date(row.importedAt);
      return (
        importedDate.getDate() === now.getDate() &&
        importedDate.getMonth() === now.getMonth() &&
        importedDate.getFullYear() === now.getFullYear()
      );
    }).length;
  }, [notificationRows]);

  const latestDetectionDisplay = useMemo(
    () => formatLatestDetection(latestDetection),
    [latestDetection],
  );

  const summaryItems = [
    {
      key: 'pending',
      label: 'Pendientes',
      value: candidateData.pendingCount || 0,
      helper: 'Por revisar',
    },
    {
      key: 'unread',
      label: 'Sin leer',
      value: summary.unreadCount || 0,
      helper: 'Nuevas',
    },
    {
      key: 'importedToday',
      label: 'Importadas hoy',
      value: importedToday,
      helper: 'Exitosamente',
    },
    {
      key: 'latestDetection',
      label: latestDetectionDisplay.primary,
      value: 'Última detección',
      helper: latestDetectionDisplay.secondary,
    },
  ];

  const notificationTotalPages = Math.max(
    1,
    Math.ceil(Number(candidateData.total || 0) / Number(candidateData.pageSize || 20)),
  );
  const searchTotalPages = Math.max(
    1,
    Number(searchMeta.totalPages || 0) ||
      Math.ceil(Number(searchMeta.total || 0) / Number(searchMeta.pageSize || 20)),
  );

  const notificationSelectableRows = notificationRows.filter(
    (row) => row.status === 'pending' || row.status === 'failed',
  );
  const allVisibleNotificationsSelected =
    notificationSelectableRows.length > 0 &&
    notificationSelectableRows.every((row) => selectedNotificationIds.includes(row.id));

  const searchSelectableRows = filteredSearchRows.filter(
    (row) => getSmartState(row, TAB_KEYS.SEARCH).actionKind === 'import',
  );
  const allVisibleSearchSelected =
    searchSelectableRows.length > 0 &&
    searchSelectableRows.every((row) => selectedSearchKeys.includes(row.id));

  const hasActiveNotificationFilters = Boolean(
    notificationFilters.search ||
      notificationFilters.marketplace ||
      notificationFilters.sellerId ||
      (notificationFilters.status && notificationFilters.status !== 'pending'),
  );

  const hasActiveSearchFilters = Boolean(
    searchFilters.marketplace ||
      searchFilters.sellerId ||
      searchFilters.state ||
      searchFilters.remoteStatus ||
      searchFilters.category ||
      searchFilters.minPrice ||
      searchFilters.maxPrice,
  );

  const handleApplyNotificationSearch = () => {
    setNotificationPage(1);
    setNotificationFilters((prev) => ({
      ...prev,
      search: String(prev.searchInput || '').trim(),
    }));
  };

  const handleClearNotificationFilters = () => {
    setNotificationPage(1);
    setNotificationFilters(initialNotificationFilters);
  };

  const handleClearSearchFilters = () => {
    setSearchFilters((prev) => ({
      ...initialSearchFilters,
      titleInput: prev.titleInput,
      skuInput: prev.skuInput,
      title: prev.title,
      sku: prev.sku,
    }));
  };

  const handleNotificationSearchKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    handleApplyNotificationSearch();
  };

  const handleSearchTabKeyDown = (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    setSearchPage(1);
    handleSearchMercadoLibre(1);
  };

  const handleToggleNotificationRow = (rowId) => {
    setSelectedNotificationIds((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId],
    );
  };

  const handleToggleAllNotifications = () => {
    if (allVisibleNotificationsSelected) {
      setSelectedNotificationIds((prev) =>
        prev.filter((id) => !notificationSelectableRows.some((row) => row.id === id)),
      );
      return;
    }
    setSelectedNotificationIds((prev) =>
      Array.from(new Set([...prev, ...notificationSelectableRows.map((row) => row.id)])),
    );
  };

  const handleToggleSearchRow = (rowId) => {
    setSelectedSearchKeys((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId],
    );
  };

  const handleToggleAllSearch = () => {
    if (allVisibleSearchSelected) {
      setSelectedSearchKeys((prev) =>
        prev.filter((id) => !searchSelectableRows.some((row) => row.id === id)),
      );
      return;
    }
    setSelectedSearchKeys((prev) =>
      Array.from(new Set([...prev, ...searchSelectableRows.map((row) => row.id)])),
    );
  };

  const closeImportDecisionModal = () =>
    setImportDecisionModal({
      open: false,
      source: null,
      ids: [],
      rows: [],
      option: 'use_mercury',
    });

  const getRowsRequiringStockDecision = (rows, tabKey) =>
    (Array.isArray(rows) ? rows : []).filter((row) => {
      const smartState = getSmartState(row, tabKey);
      if (smartState.key !== 'existing-sku') return false;
      const mercuryStock = getMercuryStock(row);
      const observedStock = getObservedStock(row);
      if (mercuryStock === null || observedStock === null) return false;
      return mercuryStock !== observedStock;
    });

  const maybeOpenImportDecisionModal = ({ source, ids, rows, tabKey }) => {
    const rowsNeedingDecision = getRowsRequiringStockDecision(rows, tabKey);
    if (rowsNeedingDecision.length === 0) {
      return false;
    }

    setImportDecisionModal({
      open: true,
      source,
      ids,
      rows: rowsNeedingDecision,
      option: 'use_mercury',
    });
    return true;
  };

  const applyMercuryStockToImportedListings = async (rows) => {
    const updates = (Array.isArray(rows) ? rows : [])
      .map((row) => ({
        marketplaceId: String(row?.marketplaceId || '').trim(),
        stock: getMercuryStock(row),
      }))
      .filter((entry) => entry.marketplaceId && entry.stock !== null);

    if (updates.length === 0) {
      return { updated: 0, failed: 0 };
    }

    const results = [];
    for (const update of updates) {
      try {
        const response = await stockyApi.bulkUpdateListingsInline([update], authUser);
        const batchResults = Array.isArray(response?.data?.results) ? response.data.results : [];
        if (batchResults.length > 0) {
          results.push(...batchResults);
        } else {
          results.push({
            marketplaceId: update.marketplaceId,
            status: 'FAILED',
            error: 'No result returned by bulk inline update',
          });
        }
      } catch (error) {
        results.push({
          marketplaceId: update.marketplaceId,
          status: 'FAILED',
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }

    return {
      updated: results.filter((entry) => entry?.status === 'UPDATED').length,
      failed: results.filter((entry) => entry?.status !== 'UPDATED').length,
    };
  };

  const handleImportCandidateIds = async (ids, stockDecision = 'keep_marketplace') => {
    if (!authUser || !Array.isArray(ids) || ids.length === 0) return;
    setBusyAction('notifications-import');
    setMessage(null);
    try {
      const selectedRows = notificationRows.filter((row) => ids.includes(row.id));
      const response = await stockyApi.importMarketplaceListingCandidates(ids, authUser);
      const imported = Array.isArray(response?.data?.imported) ? response.data.imported : [];
      const failed = Array.isArray(response?.data?.failed) ? response.data.failed : [];
      const stockAlignmentSummary =
        stockDecision === 'use_mercury'
          ? await applyMercuryStockToImportedListings(
              getRowsRequiringStockDecision(selectedRows, TAB_KEYS.NOTIFICATIONS),
            )
          : null;
      setMessage({
        type: failed.length > 0 ? 'warning' : 'success',
        content:
          failed.length > 0
            ? `Se importaron ${imported.length} productos y ${failed.length} quedaron con error.`
            : stockAlignmentSummary?.updated
              ? `Se importaron ${imported.length} productos y ${stockAlignmentSummary.updated} publicaciones nuevas quedaron con stock de MercuryCom.`
              : `Se importaron ${imported.length} productos correctamente.`,
      });
      setSnackbarVariant(failed.length > 0 ? 'warning' : 'success');
      setSnackbarMessage(
        failed.length > 0
          ? `Importación finalizada: ${imported.length} productos importados y ${failed.length} con error.`
          : `Importación finalizada: ${imported.length} productos importados.`,
      );
      setShowSnackbar(true);
      setSelectedNotificationIds([]);
      await loadNotifications();
    } catch (error) {
      console.error('Error importing notification candidates:', error);
      setNotificationsError({
        title: 'No se pudieron importar los productos seleccionados',
        content:
          error?.response?.data?.message ||
          'Intentá nuevamente en unos segundos.',
      });
      setSnackbarVariant('error');
      setSnackbarMessage('No se pudieron importar los productos seleccionados.');
      setShowSnackbar(true);
    } finally {
      setBusyAction('');
    }
  };

  const handleImportSearchSelected = async (keys = selectedSearchKeys, stockDecision = 'keep_marketplace') => {
    if (!authUser || !Array.isArray(keys) || keys.length === 0) return;
    setBusyAction('search-import');
    setMessage(null);
    try {
      const selectedRows = rawSearchRows.filter((row) => keys.includes(row.id));
      const groups = new Map();
      selectedRows.forEach((row) => {
        const groupKey = `${row.marketplace}:${row.tokenId}`;
        const bucket = groups.get(groupKey) || {
          tokenId: row.tokenId,
          marketplace: row.marketplace,
          marketplaceIds: [],
        };
        bucket.marketplaceIds.push(row.marketplaceId);
        groups.set(groupKey, bucket);
      });

      let importedCount = 0;
      for (const group of groups.values()) {
        await stockyApi.importMarketplaceIds(
          {
            tokenId: group.tokenId,
            marketplace: group.marketplace,
            onboardingSafeMode: true,
            marketplaceIds: group.marketplaceIds,
          },
          authUser,
        );
        importedCount += group.marketplaceIds.length;
      }
      const stockAlignmentSummary =
        stockDecision === 'use_mercury'
          ? await applyMercuryStockToImportedListings(
              getRowsRequiringStockDecision(selectedRows, TAB_KEYS.SEARCH),
            )
          : null;

      setMessage({
        type: 'success',
        content: stockAlignmentSummary?.updated
          ? `Se enviaron ${importedCount} productos a MercuryCom y ${stockAlignmentSummary.updated} publicaciones nuevas quedaron con stock de MercuryCom.`
          : `Se enviaron ${importedCount} productos al flujo de importación de MercuryCom.`,
      });
      setSnackbarVariant('success');
      setSnackbarMessage(
        stockAlignmentSummary?.updated
          ? `Importación finalizada: ${importedCount} productos enviados a MercuryCom y ${stockAlignmentSummary.updated} publicaciones actualizadas con stock de MercuryCom.`
          : `Importación finalizada: ${importedCount} productos enviados a MercuryCom.`,
      );
      setShowSnackbar(true);
      setSelectedSearchKeys([]);
      await handleSearchMercadoLibre(searchMeta.page || searchPage);
    } catch (error) {
      console.error('Error importing searched products:', error);
      setSearchError({
        title: 'No se pudieron importar los productos encontrados',
        content:
          error?.response?.data?.message ||
          'Intentá nuevamente en unos segundos.',
      });
      setSnackbarVariant('error');
      setSnackbarMessage('No se pudieron importar los productos encontrados.');
      setShowSnackbar(true);
    } finally {
      setBusyAction('');
    }
  };

  const handleDismiss = async (candidateId) => {
    if (!authUser || !candidateId) return;
    setBusyAction(candidateId);
    setMessage(null);
    try {
      await stockyApi.dismissMarketplaceListingCandidate(candidateId, authUser);
      setSelectedNotificationIds((prev) => prev.filter((id) => id !== candidateId));
      await loadNotifications();
    } catch (error) {
      console.error('Error dismissing candidate:', error);
      setNotificationsError({
        title: 'No se pudo descartar el producto',
        content:
          error?.response?.data?.message ||
          'Intentá nuevamente en unos segundos.',
      });
    } finally {
      setBusyAction('');
    }
  };

  const handleDismissSelected = async () => {
    if (!authUser || selectedNotificationIds.length === 0) return;
    setBusyAction('dismiss-selected');
    setMessage(null);
    try {
      await Promise.all(
        selectedNotificationIds.map((candidateId) =>
          stockyApi.dismissMarketplaceListingCandidate(candidateId, authUser),
        ),
      );
      setSelectedNotificationIds([]);
      setMessage({
        type: 'success',
        content: 'Los productos seleccionados se descartaron correctamente.',
      });
      await loadNotifications();
    } catch (error) {
      console.error('Error dismissing selected notification candidates:', error);
      setNotificationsError({
        title: 'No se pudieron descartar todos los productos',
        content:
          error?.response?.data?.message ||
          'Intentá nuevamente en unos segundos.',
      });
    } finally {
      setBusyAction('');
    }
  };

  const handleMarkAllReviewed = async () => {
    if (!authUser) return;
    setBusyAction('read-all');
    setMessage(null);
    try {
      await stockyApi.markAllNotificationsRead(authUser);
      await loadNotifications();
    } catch (error) {
      console.error('Error marking notifications as reviewed:', error);
      setNotificationsError({
        title: 'No se pudieron marcar como revisados',
        content:
          error?.response?.data?.message ||
          'Intentá nuevamente en unos segundos.',
      });
    } finally {
      setBusyAction('');
    }
  };

  const handleRefreshNotifications = async () => {
    setMessage(null);
    await loadNotifications();
  };

  const handleRefreshSearch = async () => {
    setMessage(null);
    if (!searchHasExecuted || (!searchMeta.title && !searchMeta.sku)) return;
    await handleSearchMercadoLibre(searchPage);
  };

  const handleConfirmImportDecision = async () => {
    const ids = Array.isArray(importDecisionModal.ids) ? importDecisionModal.ids : [];
    const option = importDecisionModal.option || 'keep_marketplace';
    const source = importDecisionModal.source;
    closeImportDecisionModal();

    if (source === 'notifications') {
      await handleImportCandidateIds(ids, option);
      return;
    }

    if (source === 'search') {
      await handleImportSearchSelected(ids, option);
    }
  };

  const openRowDetail = (row) => {
    setSelectedCandidate(row);
  };

  const handlePrimaryNotificationAction = async (row) => {
    const smartState = getSmartState(row, TAB_KEYS.NOTIFICATIONS);
    if (smartState.actionKind === 'detail') {
      openRowDetail(row);
      return;
    }
    if (
      maybeOpenImportDecisionModal({
        source: 'notifications',
        ids: [row.id],
        rows: [row],
        tabKey: TAB_KEYS.NOTIFICATIONS,
      })
    ) {
      return;
    }
    await handleImportCandidateIds([row.id]);
  };

  const handlePrimarySearchAction = async (row) => {
    const smartState = getSmartState(row, TAB_KEYS.SEARCH);
    if (smartState.actionKind === 'detail') {
      openRowDetail(row);
      return;
    }
    if (
      maybeOpenImportDecisionModal({
        source: 'search',
        ids: [row.id],
        rows: [row],
        tabKey: TAB_KEYS.SEARCH,
      })
    ) {
      return;
    }
    await handleImportSearchSelected([row.id]);
  };

  const renderErrorState = (error, onRetry, loading) => {
    if (!error) return null;
    return (
      <div style={{ ...errorStateStyle, marginBottom: 18 }}>
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
            <Icon name="warning circle" color="violet" size="large" style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 800, color: '#4C1D95', marginBottom: 4 }}>
                {error.title}
              </div>
              <div style={{ color: '#6B7280' }}>{error.content}</div>
            </div>
          </div>
          <Button
            basic
            color="violet"
            style={secondaryButtonStyle}
            onClick={onRetry}
            loading={loading}
            content="Reintentar"
          />
        </div>
      </div>
    );
  };

  const renderRow = (row, tabKey) => {
    const smartState = getSmartState(row, tabKey);
    const canSelect =
      tabKey === TAB_KEYS.SEARCH
        ? smartState.actionKind === 'import'
        : row.status === 'pending' || row.status === 'failed';
    const isSelected =
      tabKey === TAB_KEYS.SEARCH
        ? selectedSearchKeys.includes(row.id)
        : selectedNotificationIds.includes(row.id);
    const price = formatCurrency(getObservedPrice(row));
    const stock = getObservedStock(row);
    const thumbnailUrl = getThumbnailUrl(row);
    const primaryActionLabel = smartState.action;
    const marketplaceLogo = getMarketplaceLogo(row.marketplace);
    const hasPrimaryImportAction = smartState.actionKind === 'import';

    return (
      <div key={row.id} style={listRowStyle}>
        <div style={listRowGridStyle}>
          <div>
            <Checkbox
              checked={isSelected}
              onChange={() =>
                tabKey === TAB_KEYS.SEARCH
                  ? handleToggleSearchRow(row.id)
                  : handleToggleNotificationRow(row.id)
              }
              disabled={!canSelect}
            />
          </div>

          <div style={thumbnailTileStyle}>
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt={getProductTitle(row)}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 4,
                }}
              >
                <Icon name={getMarketplaceIconName(row.marketplace)} size="large" />
                <span style={{ fontSize: 16, fontWeight: 800 }}>{getThumbnailLabel(row)}</span>
              </div>
            )}
          </div>

          <div style={{ minWidth: 0 }}>
            <div style={itemTitleStyle}>{getProductTitle(row)}</div>
            <div style={{ ...secondaryMetaStyle, marginTop: 6 }}>
              SKU en canal: {row.sku || row.marketplaceId || 'Sin referencia'}
            </div>
          </div>

          <div style={{ minWidth: 0 }}>
                    <Label
                      basic
                      style={{
                        marginBottom: 8,
                        borderRadius: 999,
                        fontSize: 12,
                        fontWeight: 600,
                        padding: '6px 10px',
                        border: '1px solid',
                        ...subtleBadgeStyle(smartState.badgeColor),
                      }}
                    >
                      {smartState.label}
                    </Label>
            <div style={infoMetaStyle}>{smartState.helper}</div>
            {tabKey === TAB_KEYS.NOTIFICATIONS && row.lastError ? (
              <div style={{ color: '#B91C1C', marginTop: 8, fontSize: 13 }}>
                {row.lastError}
              </div>
            ) : null}
          </div>

          <div style={channelCellStyle}>
            {marketplaceLogo ? (
              <img
                src={marketplaceLogo}
                alt={getMarketplaceLabel(row.marketplace)}
                style={{ width: 22, height: 22, objectFit: 'contain', marginTop: 2 }}
              />
            ) : (
              <Icon name={getMarketplaceIconName(row.marketplace)} color="violet" />
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, color: '#111827' }}>
                {getMarketplaceLabel(row.marketplace)}
              </div>
              <div style={secondaryMetaStyle}>
                Seller: {row.sellerName || row.sellerId || '-'}
              </div>
            </div>
          </div>

          <div style={infoMetaStyle}>
            <div>{price ? `Precio: ${price}` : 'Precio: -'}</div>
            <div style={{ marginTop: 6 }}>
              {stock !== null ? `Stock: ${stock}` : 'Stock: -'}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              gap: 10,
              alignItems: 'center',
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            {hasPrimaryImportAction ? (
              <Button
                color="violet"
                style={{ ...primaryButtonStyle, ...rowActionButtonStyle }}
                disabled={!canSelect}
                loading={busyAction === 'notifications-import' || busyAction === 'search-import'}
                onClick={() =>
                  tabKey === TAB_KEYS.SEARCH
                    ? handlePrimarySearchAction(row)
                    : handlePrimaryNotificationAction(row)
                }
                content={primaryActionLabel}
              />
            ) : null}
            <Button
              basic
              color="violet"
              style={{ ...secondaryButtonStyle, ...rowActionButtonStyle }}
              onClick={() => openRowDetail(row)}
              content="Ver detalle"
            />
            {tabKey === TAB_KEYS.NOTIFICATIONS ? (
              <Dropdown
                button
                basic
                floating
                icon="ellipsis horizontal"
                className="icon"
              >
                <Dropdown.Menu>
                  <Dropdown.Item
                    text="Descartar"
                    icon="trash alternate outline"
                    disabled={row.status === 'imported'}
                    onClick={() => handleDismiss(row.id)}
                  />
                </Dropdown.Menu>
              </Dropdown>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <Container className="new-items-page" style={pageBackgroundStyle}>
      <style>{`
        .new-items-page .ui.input > input,
        .new-items-page .ui.selection.dropdown,
        .new-items-page .ui.search.dropdown > input.search {
          min-height: 44px !important;
          border-radius: 10px !important;
          border-color: #E5E7EB !important;
          box-shadow: none !important;
          background: #FFFFFF !important;
        }
        .new-items-page .ui.input > input:focus,
        .new-items-page .ui.selection.dropdown:focus,
        .new-items-page .ui.selection.dropdown.active,
        .new-items-page .ui.selection.dropdown.visible {
          border-color: #C4B5FD !important;
          box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.08) !important;
        }
        .new-items-page .ui.selection.dropdown {
          display: flex !important;
          align-items: center !important;
        }
        .new-items-page .ui.basic.button:disabled,
        .new-items-page .ui.button:disabled {
          opacity: 0.55 !important;
        }
        .new-items-page .ui.pagination.menu {
          border-radius: 12px !important;
          border: 1px solid #E5E7EB !important;
          box-shadow: none !important;
        }
        .new-items-page .ui.pagination.menu .item {
          min-width: 40px !important;
          color: #4B5563 !important;
        }
        .new-items-page .ui.pagination.menu .active.item {
          background: #F4EEFF !important;
          color: #6D28D9 !important;
          font-weight: 600 !important;
        }
      `}</style>
      <Segment style={panelFrameStyle}>
        <div style={headerWrapStyle}>
          <div style={headerAccentStyle} />
          <div style={headerRowStyle}>
            <div style={headerLeadStyle}>
              <div style={headerIconStyle}>
                <Icon name="bell outline" size="large" />
              </div>
              <div>
                <div style={headerTitleStyle}>Nuevos items</div>
                <div style={headerDescriptionStyle}>
                  Detectamos productos en tus canales que aún no están en MercuryCom.
                </div>
              </div>
            </div>

          </div>
        </div>

        <div style={{ ...pageBodyStyle, padding: 20, background: '#F8F9FB' }}>
          {message ? (
            <Message
              positive={message.type === 'success'}
              warning={message.type === 'warning'}
              content={message.content}
              style={{
                borderRadius: 14,
                boxShadow: '0 6px 18px rgba(15, 23, 42, 0.04)',
                border: '1px solid #E5E7EB',
              }}
            />
          ) : null}

          <Segment style={{ ...tabsWrapStyle, display: 'none' }}>
            <div
              role="button"
              tabIndex={0}
              style={tabButtonStyle(activeTab === TAB_KEYS.NOTIFICATIONS)}
              onClick={() => setActiveTab(TAB_KEYS.NOTIFICATIONS)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setActiveTab(TAB_KEYS.NOTIFICATIONS);
                }
              }}
            >
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>
                  Notificaciones
                </div>
                <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
                  Resolvé rápido lo detectado automáticamente
                </div>
              </div>
              <Label
                circular
                color="violet"
                style={{
                  minWidth: 28,
                  height: 28,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  boxShadow: 'none',
                }}
              >
                {candidateData.pendingCount || 0}
              </Label>
            </div>

            <div
              role="button"
              tabIndex={0}
              style={tabButtonStyle(activeTab === TAB_KEYS.SEARCH)}
              onClick={() => setActiveTab(TAB_KEYS.SEARCH)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  setActiveTab(TAB_KEYS.SEARCH);
                }
              }}
            >
              <div>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#111827' }}>
                  Buscar productos
                </div>
                <div style={{ fontSize: 13, color: '#6B7280', marginTop: 4 }}>
                  Explorá manualmente Mercado Libre y tus canales
                </div>
              </div>
              <Icon name="search" color="violet" />
            </div>
          </Segment>

          {activeTab === TAB_KEYS.NOTIFICATIONS ? (
            <>
              {renderErrorState(
                notificationsError,
                handleRefreshNotifications,
                loadingNotifications,
              )}

              <Segment style={summaryWrapperStyle}>
                <Grid divided stackable columns={4} style={{ margin: 0 }}>
                  {summaryItems.map((item) => {
                    const visual = getSummaryVisual(item.key);
                    const primaryValue = item.key === 'latestDetection' ? item.label : item.value;
                    const secondaryValue = item.key === 'latestDetection' ? item.value : item.label;
                    return (
                      <Grid.Column
                        key={item.key}
                        tablet={8}
                        computer={4}
                        mobile={16}
                        style={summaryColumnStyle}
                      >
                        <div style={summaryMetricStyle}>
                          <div
                            style={{
                              ...summaryIconBoxStyle,
                              background: visual.background,
                              color: visual.color,
                            }}
                          >
                            <Icon name={visual.icon} size="large" />
                          </div>
                          <div>
                            <div style={summaryCountStyle}>{primaryValue}</div>
                            <div style={summaryLabelStyle}>{secondaryValue}</div>
                            <div style={summaryHelperStyle}>{item.helper}</div>
                          </div>
                        </div>
                      </Grid.Column>
                    );
                  })}
                </Grid>
              </Segment>

              <Segment style={actionBarStyle}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 12,
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button
                      color="violet"
                      style={primaryButtonStyle}
                      disabled={selectedNotificationIds.length === 0}
                      loading={busyAction === 'notifications-import'}
                      onClick={() => {
                        const rows = notificationRows.filter((row) =>
                          selectedNotificationIds.includes(row.id),
                        );
                        if (
                          maybeOpenImportDecisionModal({
                            source: 'notifications',
                            ids: selectedNotificationIds,
                            rows,
                            tabKey: TAB_KEYS.NOTIFICATIONS,
                          })
                        ) {
                          return;
                        }
                        handleImportCandidateIds(selectedNotificationIds);
                      }}
                      content="Importar seleccionados"
                    />
                    <Button
                      basic
                      color="grey"
                      style={neutralButtonStyle}
                      disabled={selectedNotificationIds.length === 0}
                      loading={busyAction === 'dismiss-selected'}
                      onClick={handleDismissSelected}
                      content="Descartar seleccionados"
                    />
                    <Button
                      basic
                      color="violet"
                      style={secondaryButtonStyle}
                      disabled={(summary.unreadCount || 0) === 0}
                      loading={busyAction === 'read-all'}
                      onClick={handleMarkAllReviewed}
                      content="Marcar como revisados"
                    />
                  </div>

                  <Button
                    basic
                    color="violet"
                    style={secondaryButtonStyle}
                    icon
                    labelPosition="left"
                    onClick={handleRefreshNotifications}
                    loading={loadingNotifications}
                  >
                    <Icon name="refresh" />
                    Actualizar
                  </Button>
                </div>
              </Segment>

              <Segment style={filterBarStyle}>
                <Grid stackable verticalAlign="middle" columns={16}>
                  <Grid.Column computer={5} tablet={16} mobile={16}>
                    <Input
                      fluid
                      icon="search"
                      placeholder="Buscar por título, SKU o referencia"
                      value={notificationFilters.searchInput}
                      onKeyDown={handleNotificationSearchKeyDown}
                      onChange={(event) =>
                        setNotificationFilters((prev) => ({
                          ...prev,
                          searchInput: event.target.value,
                        }))
                      }
                      action={{
                        color: 'violet',
                        icon: 'search',
                        content: 'Buscar',
                        onClick: handleApplyNotificationSearch,
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column computer={3} tablet={8} mobile={16}>
                    <Dropdown
                      fluid
                      selection
                      options={marketplaceOptions}
                      placeholder="Canal"
                      value={notificationFilters.marketplace}
                      onChange={(_, data) => {
                        setNotificationPage(1);
                        setNotificationFilters((prev) => ({
                          ...prev,
                          marketplace: data.value || '',
                          sellerId: '',
                        }));
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column computer={3} tablet={8} mobile={16}>
                    <Dropdown
                      fluid
                      selection
                      options={notificationStatusOptions}
                      placeholder="Estado"
                      value={notificationFilters.status}
                      onChange={(_, data) => {
                        setNotificationPage(1);
                        setNotificationFilters((prev) => ({
                          ...prev,
                          status: data.value || '',
                        }));
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column computer={3} tablet={8} mobile={16}>
                    <Dropdown
                      fluid
                      selection
                      options={notificationSellerOptions}
                      placeholder="Seller"
                      value={notificationFilters.sellerId}
                      onChange={(_, data) => {
                        setNotificationPage(1);
                        setNotificationFilters((prev) => ({
                          ...prev,
                          sellerId: data.value || '',
                        }));
                      }}
                    />
                  </Grid.Column>
                  <Grid.Column computer={2} tablet={8} mobile={16}>
                    <Button
                      basic
                      color="violet"
                      style={secondaryButtonStyle}
                      icon
                      labelPosition="left"
                      fluid
                      disabled={!hasActiveNotificationFilters}
                      onClick={handleClearNotificationFilters}
                    >
                      <Icon name="filter" />
                      Filtros
                    </Button>
                  </Grid.Column>
                </Grid>
              </Segment>

              <Segment style={listPanelStyle}>
                <div style={listHeaderStyle}>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>
                      Productos detectados ({candidateData.total || notificationRows.length})
                    </div>
                    <div style={{ color: '#6B7280', fontSize: 13, marginTop: 4 }}>
                      Revisá productos nuevos, listings pendientes o importaciones con error.
                    </div>
                  </div>

                  {notificationRows.length > 0 ? (
                    <Checkbox
                      checked={allVisibleNotificationsSelected}
                      onChange={handleToggleAllNotifications}
                      disabled={notificationSelectableRows.length === 0}
                      label="Seleccionar visibles"
                    />
                  ) : null}
                </div>

                <div style={listScrollWrapStyle}>
                  <div style={{ minWidth: 1180 }}>

                {notificationRows.length > 0 ? (
                  <div style={listColumnHintStyle}>
                    <span />
                    <span />
                    <span>Producto</span>
                    <span>Estado</span>
                    <span>Canal</span>
                    <span>Información</span>
                    <span>Acciones</span>
                  </div>
                ) : null}

                {loadingNotifications && notificationRows.length === 0 ? (
                  <div style={emptyStateStyle}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#4C1D95' }}>
                      Cargando notificaciones...
                    </div>
                    <div style={{ marginTop: 8, color: '#6B7280' }}>
                      En unos segundos vas a poder resolver los productos detectados desde esta pantalla.
                    </div>
                  </div>
                ) : notificationRows.length === 0 ? (
                  <div style={emptyStateStyle}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#4C1D95' }}>
                      Todo al día
                    </div>
                    <div style={{ marginTop: 8, color: '#6B7280' }}>
                      No hay nuevos items pendientes de revisión.
                    </div>
                    <Button
                      color="violet"
                      style={{ ...primaryButtonStyle, marginTop: 16 }}
                      onClick={() => setActiveTab(TAB_KEYS.SEARCH)}
                      content="Buscar productos manualmente"
                    />
                  </div>
                ) : (
                  <div>{notificationRows.map((row) => renderRow(row, TAB_KEYS.NOTIFICATIONS))}</div>
                )}

                {notificationRows.length > 0 ? (
                  <div style={footerRowStyle}>
                    <div style={{ color: '#64748B' }}>
                      Mostrando {notificationRows.length} de {candidateData.total} resultados
                    </div>
                    <Pagination
                      activePage={notificationPage}
                      totalPages={notificationTotalPages}
                      onPageChange={(_, data) => setNotificationPage(Number(data.activePage || 1))}
                      />
                    </div>
                  ) : null}
                  </div>
                </div>
              </Segment>
            </>
          ) : (
            <>
              {renderErrorState(searchError, handleRefreshSearch, searchLoading)}

              <Segment style={searchHeroStyle} onKeyDown={handleSearchTabKeyDown}>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>
                  Buscar productos en tus canales
                </div>
                <div style={{ color: '#6B7280', marginTop: 6, marginBottom: 18 }}>
                  Encontrá productos en Mercado Libre o Tienda Nube para importarlos o revisarlos.
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <Input
                    fluid
                    size="large"
                    placeholder="Buscar por título"
                    value={searchFilters.titleInput}
                    onChange={(event) =>
                      setSearchFilters((prev) => ({
                        ...prev,
                        titleInput: event.target.value,
                      }))
                    }
                    style={{ flex: 1, minWidth: 220 }}
                  />
                  <Input
                    fluid
                    size="large"
                    placeholder="Buscar por SKU"
                    value={searchFilters.skuInput}
                    onChange={(event) =>
                      setSearchFilters((prev) => ({
                        ...prev,
                        skuInput: event.target.value,
                      }))
                    }
                    style={{ flex: 1, minWidth: 180 }}
                  />
                  <Button
                    color="violet"
                    size="large"
                    loading={searchLoading}
                    onClick={() => {
                      setSearchPage(1);
                      handleSearchMercadoLibre(1);
                    }}
                    content="Buscar"
                  />
                </div>

                <Grid stackable columns={16} style={{ marginTop: 10 }}>
                  <Grid.Column computer={3} tablet={8} mobile={16}>
                    <Dropdown
                      fluid
                      selection
                      options={marketplaceOptions}
                      placeholder="Canal"
                      value={searchFilters.marketplace}
                      onChange={(_, data) =>
                        setSearchFilters((prev) => ({
                          ...prev,
                          marketplace: data.value || '',
                          sellerId: '',
                        }))
                      }
                    />
                  </Grid.Column>
                  <Grid.Column computer={3} tablet={8} mobile={16}>
                    <Dropdown
                      fluid
                      selection
                      options={searchSellerOptions}
                      placeholder="Seller"
                      value={searchFilters.sellerId}
                      onChange={(_, data) =>
                        setSearchFilters((prev) => ({
                          ...prev,
                          sellerId: data.value || '',
                        }))
                      }
                    />
                  </Grid.Column>
                  <Grid.Column computer={3} tablet={8} mobile={16}>
                    <Dropdown
                      fluid
                      selection
                      options={searchMercuryStateOptions}
                      placeholder="Estado MercuryCom"
                      value={searchFilters.state}
                      onChange={(_, data) =>
                        setSearchFilters((prev) => ({
                          ...prev,
                          state: data.value || '',
                        }))
                      }
                    />
                  </Grid.Column>
                  <Grid.Column computer={2} tablet={8} mobile={16}>
                    <Dropdown
                      fluid
                      selection
                      options={searchRemoteStatusOptions}
                      placeholder="Estado remoto"
                      value={searchFilters.remoteStatus}
                      onChange={(_, data) =>
                        setSearchFilters((prev) => ({
                          ...prev,
                          remoteStatus: data.value || '',
                        }))
                      }
                    />
                  </Grid.Column>
                  <Grid.Column computer={2} tablet={8} mobile={16}>
                    <Dropdown
                      fluid
                      selection
                      options={categoryOptions}
                      placeholder="Categoría"
                      value={searchFilters.category}
                      onChange={(_, data) =>
                        setSearchFilters((prev) => ({
                          ...prev,
                          category: data.value || '',
                        }))
                      }
                    />
                  </Grid.Column>
                  <Grid.Column computer={4} tablet={16} mobile={16}>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <Button
                        basic
                        color="violet"
                        style={secondaryButtonStyle}
                        icon
                        labelPosition="left"
                        onClick={() => setSearchAdvancedOpen((prev) => !prev)}
                      >
                        <Icon name="sliders horizontal" />
                        Más filtros
                      </Button>
                      <Dropdown
                        selection
                        compact
                        options={searchSortOptions}
                        value={searchFilters.sort}
                        onChange={(_, data) =>
                          setSearchFilters((prev) => ({
                            ...prev,
                            sort: data.value || 'recent',
                          }))
                        }
                      />
                    </div>
                  </Grid.Column>
                </Grid>

                {searchAdvancedOpen ? (
                  <Grid stackable columns={16} style={{ marginTop: 6 }}>
                    <Grid.Column computer={4} tablet={8} mobile={16}>
                      <Input
                        fluid
                        type="number"
                        min="0"
                        placeholder="Precio mínimo"
                        value={searchFilters.minPrice}
                        onChange={(event) =>
                          setSearchFilters((prev) => ({
                            ...prev,
                            minPrice: event.target.value,
                          }))
                        }
                      />
                    </Grid.Column>
                    <Grid.Column computer={4} tablet={8} mobile={16}>
                      <Input
                        fluid
                        type="number"
                        min="0"
                        placeholder="Precio máximo"
                        value={searchFilters.maxPrice}
                        onChange={(event) =>
                          setSearchFilters((prev) => ({
                            ...prev,
                            maxPrice: event.target.value,
                          }))
                        }
                      />
                    </Grid.Column>
                    <Grid.Column computer={8} tablet={16} mobile={16}>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
                        <Button
                        basic
                        color="grey"
                        style={neutralButtonStyle}
                          disabled={!hasActiveSearchFilters}
                          onClick={handleClearSearchFilters}
                        >
                          Limpiar filtros
                        </Button>
                      </div>
                    </Grid.Column>
                  </Grid>
                ) : null}
              </Segment>

              {!searchHasExecuted ? (
                <Segment style={listPanelStyle}>
                  <div style={emptyStateStyle}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#4C1D95' }}>
                      Buscá productos en tus canales
                    </div>
                    <div style={{ marginTop: 8, color: '#6B7280' }}>
                      Ingresá un título, SKU o referencia para encontrar productos de MercadoLibre o Tienda Nube.
                    </div>
                  </div>
                </Segment>
              ) : (
                <>
                  <Segment style={actionBarStyle}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 12,
                        flexWrap: 'wrap',
                      }}
                    >
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button
                          color="violet"
                          style={primaryButtonStyle}
                          disabled={selectedSearchKeys.length === 0}
                          loading={busyAction === 'search-import'}
                          onClick={() => {
                            const rows = rawSearchRows.filter((row) =>
                              selectedSearchKeys.includes(row.id),
                            );
                            if (
                              maybeOpenImportDecisionModal({
                                source: 'search',
                                ids: selectedSearchKeys,
                                rows,
                                tabKey: TAB_KEYS.SEARCH,
                              })
                            ) {
                              return;
                            }
                            handleImportSearchSelected(selectedSearchKeys);
                          }}
                          content="Importar seleccionados"
                        />
                      </div>

                      <Button
                        basic
                        color="violet"
                        style={secondaryButtonStyle}
                        icon
                        labelPosition="left"
                        onClick={handleRefreshSearch}
                        loading={searchLoading}
                      >
                        <Icon name="refresh" />
                        Actualizar
                      </Button>
                    </div>
                  </Segment>

                  <Segment style={listPanelStyle}>
                    <div style={listHeaderStyle}>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: '#111827' }}>
                          Resultados de la búsqueda ({filteredSearchRows.length})
                        </div>
                        <div style={{ color: '#6B7280', fontSize: 13, marginTop: 4 }}>
                          Mostrando productos encontrados en tus canales que no están en MercuryCom o requieren revisión.
                        </div>
                      </div>

                      {filteredSearchRows.length > 0 ? (
                        <Checkbox
                          checked={allVisibleSearchSelected}
                          onChange={handleToggleAllSearch}
                          disabled={searchSelectableRows.length === 0}
                          label="Seleccionar visibles"
                        />
                      ) : null}
                    </div>

                    <div style={listScrollWrapStyle}>
                      <div style={{ minWidth: 1180 }}>
                    {filteredSearchRows.length > 0 ? (
                      <div style={listColumnHintStyle}>
                        <span />
                        <span />
                        <span>Producto</span>
                        <span>Estado</span>
                        <span>Canal</span>
                        <span>Información</span>
                        <span>Acciones</span>
                      </div>
                    ) : null}

                    {searchLoading && filteredSearchRows.length === 0 ? (
                      <div style={emptyStateStyle}>
                        <div style={{ fontSize: 18, fontWeight: 800, color: '#4C1D95' }}>
                          Buscando productos...
                        </div>
                        <div style={{ marginTop: 8, color: '#6B7280' }}>
                          En unos segundos vas a ver los resultados encontrados en tus canales.
                        </div>
                      </div>
                    ) : filteredSearchRows.length === 0 ? (
                      <div style={emptyStateStyle}>
                        <div style={{ fontSize: 22, fontWeight: 800, color: '#4C1D95' }}>
                          No encontramos productos
                        </div>
                        <div style={{ marginTop: 8, color: '#6B7280' }}>
                          Probá ajustar la búsqueda o cambiar los filtros.
                        </div>
                        <Button
                          basic
                          color="violet"
                          style={{ ...secondaryButtonStyle, marginTop: 16 }}
                          onClick={handleClearSearchFilters}
                          content="Limpiar filtros"
                        />
                      </div>
                    ) : (
                      <div>{filteredSearchRows.map((row) => renderRow(row, TAB_KEYS.SEARCH))}</div>
                    )}

                    {searchHasExecuted && filteredSearchRows.length > 0 ? (
                      <div style={footerRowStyle}>
                        <div style={{ color: '#64748B' }}>
                          Mostrando {filteredSearchRows.length} de {searchMeta.total} resultados
                        </div>
                        <Pagination
                          activePage={searchPage}
                          totalPages={searchTotalPages}
                          onPageChange={(_, data) => {
                            const nextPage = Number(data.activePage || 1);
                            setSearchPage(nextPage);
                            handleSearchMercadoLibre(nextPage);
                          }}
                        />
                      </div>
                    ) : null}
                      </div>
                    </div>
                  </Segment>
                </>
              )}
            </>
          )}
        </div>
      </Segment>

      <Modal
        open={importDecisionModal.open}
        onClose={closeImportDecisionModal}
        size={importDecisionModal.rows.length > 1 ? 'large' : 'small'}
      >
        <Modal.Header>
          {importDecisionModal.rows.length > 1
            ? `Importar ${importDecisionModal.rows.length} productos seleccionados`
            : 'Importar producto'}
        </Modal.Header>
        <Modal.Content>
          {importDecisionModal.rows.length > 0 ? (
            <Grid stackable columns={importDecisionModal.rows.length > 1 ? 2 : 1}>
              <Grid.Column width={importDecisionModal.rows.length > 1 ? 11 : 16}>
                <Message
                  info
                  style={{ background: '#FAF5FF', border: '1px solid #E9D5FF', color: '#5B21B6' }}
                >
                  {importDecisionModal.rows.length > 1
                    ? 'Los productos seleccionados tienen diferencias de stock entre el canal y MercuryCom. Elegí cómo querés importar esas publicaciones nuevas.'
                    : 'Esta publicación nueva tiene un stock distinto al registrado en MercuryCom. Elegí cómo querés importarla.'}
                </Message>

                {importDecisionModal.rows.length === 1 ? (
                  <div style={{ marginBottom: 20 }}>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16 }}>
                      <div style={thumbnailTileStyle}>
                        {getThumbnailUrl(importDecisionModal.rows[0]) ? (
                          <img
                            src={getThumbnailUrl(importDecisionModal.rows[0])}
                            alt={getProductTitle(importDecisionModal.rows[0])}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : null}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={itemTitleStyle}>{getProductTitle(importDecisionModal.rows[0])}</div>
                        <div style={{ ...secondaryMetaStyle, marginTop: 6 }}>
                          SKU del canal: {importDecisionModal.rows[0].sku || 'Sin SKU'}
                        </div>
                      </div>
                    </div>

                    <Grid stackable columns={2}>
                      <Grid.Column>
                        <Segment style={{ ...surfaceCardStyle, padding: 18 }}>
                          <div style={{ ...secondaryMetaStyle, marginBottom: 8 }}>
                            Stock en {getMarketplaceLabel(importDecisionModal.rows[0].marketplace)}
                          </div>
                          <div style={{ fontSize: 28, fontWeight: 800, color: '#7C3AED' }}>
                            {getObservedStock(importDecisionModal.rows[0]) ?? '-'} unidades
                          </div>
                        </Segment>
                      </Grid.Column>
                      <Grid.Column>
                        <Segment style={{ ...surfaceCardStyle, padding: 18 }}>
                          <div style={{ ...secondaryMetaStyle, marginBottom: 8 }}>
                            Stock en MercuryCom
                          </div>
                          <div style={{ fontSize: 28, fontWeight: 800, color: '#22C55E' }}>
                            {getMercuryStock(importDecisionModal.rows[0]) ?? '-'} unidades
                          </div>
                        </Segment>
                      </Grid.Column>
                    </Grid>
                  </div>
                ) : null}

                <Grid stackable columns={2}>
                  <Grid.Column>
                    <div
                      role="button"
                      tabIndex={0}
                      style={importChoiceCardStyle(importDecisionModal.option === 'use_mercury')}
                      onClick={() =>
                        setImportDecisionModal((prev) => ({ ...prev, option: 'use_mercury' }))
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          setImportDecisionModal((prev) => ({ ...prev, option: 'use_mercury' }));
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <Icon
                          name={
                            importDecisionModal.option === 'use_mercury'
                              ? 'dot circle outline'
                              : 'circle outline'
                          }
                          color="violet"
                        />
                        <div style={{ fontWeight: 800, color: '#111827' }}>
                          Usar stock de MercuryCom
                        </div>
                      </div>
                      <div style={{ ...infoMetaStyle, lineHeight: 1.6 }}>
                        Se importará la publicación y el stock del marketplace se actualizará con el stock actual de MercuryCom.
                      </div>
                    </div>
                  </Grid.Column>
                  <Grid.Column>
                    <div
                      role="button"
                      tabIndex={0}
                      style={importChoiceCardStyle(importDecisionModal.option === 'keep_marketplace')}
                      onClick={() =>
                        setImportDecisionModal((prev) => ({ ...prev, option: 'keep_marketplace' }))
                      }
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          setImportDecisionModal((prev) => ({ ...prev, option: 'keep_marketplace' }));
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                        <Icon
                          name={
                            importDecisionModal.option === 'keep_marketplace'
                              ? 'dot circle outline'
                              : 'circle outline'
                          }
                          color="violet"
                        />
                        <div style={{ fontWeight: 800, color: '#111827' }}>
                          Mantener stock de {importDecisionModal.rows.length === 1
                            ? getMarketplaceLabel(importDecisionModal.rows[0].marketplace)
                            : 'Mercado Libre/Tienda Nube'}
                        </div>
                      </div>
                      <div style={{ ...infoMetaStyle, lineHeight: 1.6 }}>
                        Se importará la publicación sin modificar el stock actual del canal. Si queda inconsistente con MercuryCom, después vas a tener que ajustarlo manualmente.
                      </div>
                    </div>
                  </Grid.Column>
                </Grid>
              </Grid.Column>

              {importDecisionModal.rows.length > 1 ? (
                <Grid.Column width={5}>
                  <Segment style={{ ...surfaceCardStyle, padding: 18 }}>
                    <div style={{ fontWeight: 800, color: '#111827', marginBottom: 14 }}>
                      Resumen de diferencias
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {importDecisionModal.rows.map((row) => {
                        const marketplaceStock = getObservedStock(row);
                        const mercuryStock = getMercuryStock(row);
                        const difference =
                          marketplaceStock !== null && mercuryStock !== null
                            ? mercuryStock - marketplaceStock
                            : null;
                        return (
                          <div key={row.id} style={{ display: 'flex', gap: 10 }}>
                            <div style={{ ...thumbnailTileStyle, width: 52, height: 52 }}>
                              {getThumbnailUrl(row) ? (
                                <img
                                  src={getThumbnailUrl(row)}
                                  alt={getProductTitle(row)}
                                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                />
                              ) : null}
                            </div>
                            <div style={{ minWidth: 0 }}>
                              <div style={{ fontWeight: 700, color: '#111827', lineHeight: 1.3 }}>
                                {getProductTitle(row)}
                              </div>
                              <div style={{ ...secondaryMetaStyle, marginTop: 4 }}>
                                Canal: {marketplaceStock ?? '-'} · MercuryCom: {mercuryStock ?? '-'}
                              </div>
                              {difference !== null ? (
                                <div
                                  style={{
                                    marginTop: 4,
                                    color: difference === 0 ? '#6B7280' : '#DC2626',
                                    fontWeight: 700,
                                  }}
                                >
                                  Diferencia: {difference > 0 ? `+${difference}` : difference}
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </Segment>
                </Grid.Column>
              ) : null}
            </Grid>
          ) : null}
        </Modal.Content>
        <Modal.Actions>
          <Button basic color="grey" onClick={closeImportDecisionModal}>
            Cancelar
          </Button>
          <Button color="violet" onClick={handleConfirmImportDecision}>
            {importDecisionModal.rows.length > 1
              ? `Importar ${importDecisionModal.ids.length} productos`
              : 'Importar producto'}
          </Button>
        </Modal.Actions>
      </Modal>

      <Modal
        open={Boolean(selectedCandidate)}
        onClose={() => setSelectedCandidate(null)}
        size="small"
      >
        <Modal.Header>Detalle del producto</Modal.Header>
        <Modal.Content>
          {selectedCandidate ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: '#111827' }}>
                  {getProductTitle(selectedCandidate)}
                </div>
                <div style={{ color: '#64748B', marginTop: 4 }}>
                  {getMarketplaceLabel(selectedCandidate.marketplace)} ·{' '}
                  {selectedCandidate.sellerName || selectedCandidate.sellerId || '-'}
                </div>
              </div>
              <Grid stackable columns={2}>
                <Grid.Column>
                  <div style={secondaryMetaStyle}>
                    <strong>SKU:</strong> {selectedCandidate.sku || 'Sin SKU'}
                  </div>
                </Grid.Column>
                <Grid.Column>
                  <div style={secondaryMetaStyle}>
                    <strong>Referencia:</strong> {selectedCandidate.marketplaceId || '-'}
                  </div>
                </Grid.Column>
                <Grid.Column>
                  <div style={secondaryMetaStyle}>
                    <strong>Estado:</strong> {selectedCandidate.status || '-'}
                  </div>
                </Grid.Column>
                {selectedCandidate.lastDetectedAt ? (
                  <Grid.Column>
                    <div style={secondaryMetaStyle}>
                      <strong>Detectado:</strong> {formatDateTime(selectedCandidate.lastDetectedAt)}
                    </div>
                  </Grid.Column>
                ) : null}
              </Grid>
              <div>
                <div style={{ fontWeight: 700, color: '#111827', marginBottom: 6 }}>
                  Datos observados
                </div>
                <pre
                  style={{
                    margin: 0,
                    padding: 14,
                    borderRadius: 12,
                    background: '#F8FAFC',
                    border: '1px solid #E5E7EB',
                    fontSize: 12,
                    overflowX: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}
                >
                  {JSON.stringify(
                    selectedCandidate.metadata?.eventType === 'manual_search'
                      ? buildSearchDetailPayload(selectedCandidate)
                      : buildDetectedDetailPayload(selectedCandidate),
                    null,
                    2,
                  )}
                </pre>
              </div>
            </div>
          ) : null}
        </Modal.Content>
        <Modal.Actions>
          <Button basic color="grey" onClick={() => setSelectedCandidate(null)}>
            Cerrar
          </Button>
        </Modal.Actions>
      </Modal>

      <SemanticUiReactSnackbar
        variant={snackbarVariant}
        visible={showSnackbar}
        setVisible={setShowSnackbar}
      >
        {snackbarMessage}
      </SemanticUiReactSnackbar>
    </Container>
  );
}

export default NotificationsDashboard;
